'use client';

import React, { FC, memo, useState } from 'react';
import {
  CardAddress,
  CardContent,
  CardHead,
  ImageWrapper,
  ButtonsBox,
  StyledCard,
  StyledButton,
} from './styled';
import { HousingCardStats } from '@/components/shared/HousingCardStats';
import { ProjectCardStats } from '@/components/shared/HousingCardStats/ProjectCardStats';
import { ProjectObjectsButton } from './styled';
import { Typography } from '@mui/material';
import { COLORS } from '@/constants';

import { PATHS } from '@/constants';
import { IHousingObjectResponse, OfferType } from '@/types';

import { normalizeHTML, getAddress } from '@/common';
import { useWatchlistCard, useSearchParamsCompat, writeListingNavState } from '@/hooks';
import { usePathname } from 'next/navigation';

import isEqual from 'react-fast-compare';
import dynamic from 'next/dynamic';
import { BrokerRequestList, CopyIcon, DeactivatedTag, GeoIcon, HeartIcon } from '@/components';

const QuickRequestModal = dynamic(() =>
  import('@/components/modals/QuickRequestModal').then((m) => ({ default: m.QuickRequestModal })),
);
const ShareModal = dynamic(() =>
  import('@/components/modals/ShareModal').then((m) => ({ default: m.ShareModal })),
);
import { useTranslations } from 'next-intl';
const HousingCardGallerySwiper = dynamic(() =>
  import('../HousingCardGallerySwiper').then((m) => ({ default: m.HousingCardGallerySwiper })),
);
import { BrokerRequestTooltip } from '../BrokerRequestTooltip';
import Link from 'next/link';

type HousingCardProps = {
  data: IHousingObjectResponse;
  borderless?: boolean;
  showRequests?: boolean;
  id?: string;
  offerType?: string;
  fromList?: boolean;
  isDeactivated?: boolean;
  priority?: boolean;
  cardIndex?: number;
  isProject?: boolean;
};

// eslint-disable-next-line react/display-name
export const HousingCard: FC<HousingCardProps> = memo(
  ({
    data,
    borderless = false,
    fromList = false,
    showRequests,
    id,
    offerType,
    isDeactivated = false,
    priority,
    cardIndex,
    isProject,
  }) => {
    const t = useTranslations();
    const [isShareOpen, setIsShareOpen] = useState(false);
    const pathname = usePathname();
    const searchParams = useSearchParamsCompat();

    const baseSlug = `${data.slug}__${data.id}`;
    const resolvedIsProject = isProject ?? data.project;

    const { inWatchlist, toggleWatch } = useWatchlistCard(data, resolvedIsProject);
    const basePath = resolvedIsProject ? PATHS.PROJECT : PATHS.HOUSING;
    const displayTitle =
      resolvedIsProject && data.projectData?.title ? data.projectData.title : data.title;

    const listPageUrl = fromList
      ? (() => {
          const params = new URLSearchParams(searchParams);
          params.delete('fromListUrl');
          params.delete('fromDetailId');
          const search = params.toString();
          return `${pathname}${search ? `?${search}` : ''}`;
        })()
      : '';

    const slugLink = baseSlug;

    const handleCardClick = () => {
      if (fromList && listPageUrl) {
        writeListingNavState(listPageUrl, data.id);
      }
    };

    const { sell } = data ? data : { sell: false };

    const isSell = offerType ? offerType === OfferType.SELL : sell;

    return (
      <StyledCard variant="outlined" id={id}>
        <ImageWrapper $borderless={borderless} $isProject={resolvedIsProject}>
          <HousingCardGallerySwiper
            id={data.id}
            imageList={data.imageList}
            link={slugLink}
            basePath={basePath}
            images={data?.media?.image}
            priority={priority}
            cardIndex={cardIndex}
            onLinkClick={handleCardClick}
          />
          <ButtonsBox>
            {!isDeactivated && (
              <ShareModal
                id={data.id}
                title={data.title}
                media={data.imageFullUrl}
                slugLink={slugLink}
                onToggle={(open) => setIsShareOpen(open)}
                trigger={
                  <StyledButton $isActive={isShareOpen} aria-label="Share">
                    <CopyIcon
                      width={19}
                      height={19}
                      stroke="#fff"
                      isActive={isShareOpen}
                      aria-hidden="true"
                    />
                  </StyledButton>
                }
              />
            )}

            <StyledButton
              $isActive={inWatchlist}
              onClick={toggleWatch}
              aria-label={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
            >
              <HeartIcon
                isActive={inWatchlist}
                width={19}
                height={19}
                stroke="#fff"
                aria-hidden="true"
              />
            </StyledButton>
          </ButtonsBox>
          {isDeactivated ? (
            <DeactivatedTag />
          ) : (
            <BrokerRequestTooltip objectId={data.id} hasInfoRequest={data?.hasInfoRequest} />
          )}
        </ImageWrapper>
        <CardContent>
          <CardHead>
            {isDeactivated ? (
              <Typography
                fontSize={16}
                lineHeight={1.26}
                fontWeight={700}
                color={COLORS['gray-dark-2']}
                whiteSpace={'nowrap'}
                textOverflow={'ellipsis'}
                overflow={'hidden'}
              >
                {normalizeHTML(displayTitle)}
              </Typography>
            ) : (
              <Link href={`/${basePath}/${slugLink}`} onClick={handleCardClick}>
                <Typography
                  fontSize={16}
                  lineHeight={1.26}
                  fontWeight={700}
                  color={COLORS['blue-light-2']}
                  whiteSpace={'nowrap'}
                  textOverflow={'ellipsis'}
                  overflow={'hidden'}
                >
                  {normalizeHTML(displayTitle)}
                </Typography>
              </Link>
            )}
            <CardAddress>
              <GeoIcon width={14} height={12} />
              <Typography
                textTransform={'capitalize'}
                fontSize={13}
                lineHeight={1}
                fontWeight={500}
                color={COLORS['gray-dark']}
              >
                {getAddress({ address: data.address, postcode: data.postcode, place: data.place })}
              </Typography>
            </CardAddress>
          </CardHead>

          <Typography
            fontWeight={400}
            fontSize={14}
            lineHeight={'145%'}
            sx={{
              display: 'none',
            }}
          >
            {normalizeHTML(data?.description)}
          </Typography>

          {resolvedIsProject ? (
            <ProjectCardStats data={data} offerType={offerType} />
          ) : (
            <HousingCardStats
              prices={data.prices}
              squares={data.squares}
              otherDetails={data.otherDetails}
              isSell={isSell}
            />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {!resolvedIsProject && showRequests && (
              <BrokerRequestList brokerData={data.brokerData} objectId={data.id} />
            )}
            {(data?.brokerData?.contactEmail ||
              data?.brokerData?.officeName ||
              data?.brokerData?.name) && (
              <QuickRequestModal
                objectId={data.id}
                whatsappDisabled={data.whatsappDisabled}
                hasInfoRequest={data.hasInfoRequest}
                brokerData={{
                  imageFullUrl: data?.brokerData?.imageFullUrl,
                  name: data?.brokerData?.name,
                  officeName: data?.brokerData?.officeName,
                }}
                hideButton={isDeactivated}
              />
            )}
            {resolvedIsProject && (
              <ProjectObjectsButton href={`/${basePath}/${slugLink}`} onClick={handleCardClick}>
                {data.projectData?.immoNumFound === 1
                  ? t('projectDetails.availableObject')
                  : t('projectDetails.availableObjects', {
                      count: data.projectData?.immoNumFound ?? 0,
                    })}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3.3335 8H12.6668"
                    stroke="#4F689A"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 3.3335L12.6667 8.00016L8 12.6668"
                    stroke="#4F689A"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </ProjectObjectsButton>
            )}
          </div>
        </CardContent>
      </StyledCard>
    );
  },
  isEqual,
);
